import React, { useState, useEffect } from 'react';
import { runGroupDeletion, purgeGroupProofs, fetchAttestedMemberNames } from '../../../shared/utils/groupDeletion';

// Permanent deletion of an ARCHIVED group (Stage 39). Guards, in order:
//   1. Export-first — if any member has a certified record, warn (they should export
//      their self-verifying PDF first; the record is destroyed here, their PDF is not).
//   2. Type-to-confirm — the admin must type the exact group name.
//   3. On confirm: admin_delete_group('hard_delete', <name token>) then purge the proof
//      blobs from Storage (the RPC removes DB rows; only the client can remove objects).
// All member-facing copy below is FRANNIE DRAFT — finalize wording with Frannie.
const DeleteGroupModal = ({ group, onClose, onDeleted }) => {
  const [attestedNames, setAttestedNames] = useState(null); // null = loading
  const [typed, setTyped] = useState('');
  const [ack, setAck] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let on = true;
    fetchAttestedMemberNames(group.id)
      .then(n => { if (on) setAttestedNames(n); })
      .catch(() => { if (on) setAttestedNames([]); });
    return () => { on = false; };
  }, [group.id]);

  const hasAttested = Array.isArray(attestedNames) && attestedNames.length > 0;
  const nameOk = typed.trim() === group.group_name;
  const canDelete = nameOk && (!hasAttested || ack) && !working && attestedNames !== null;

  const handleDelete = async () => {
    setWorking(true); setError(null);
    try {
      const result = await runGroupDeletion(group.id, 'hard_delete', typed.trim());
      const purge = await purgeGroupProofs(result);
      onDeleted(group.id, purge);
    } catch (err) {
      setError(err.message || 'Could not delete the group.');
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-error-700">Delete group permanently</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto">
          {/* FRANNIE DRAFT */}
          <p className="text-sm text-neutral-700">
            This permanently deletes <strong>{group.group_name}</strong> and all its contributions,
            rounds, and member data. This cannot be undone. The organizer attestations and the audit
            trail are preserved as a tamper-evident record.
          </p>

          {/* Export-first prompt */}
          {attestedNames === null ? (
            <p className="text-xs text-neutral-400">Checking for certified records…</p>
          ) : hasAttested && (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
              {/* FRANNIE DRAFT */}
              <p className="text-xs font-semibold text-warning-800 mb-1">Export certified records first</p>
              <p className="text-xs text-warning-700 mb-2">
                These members have a certified record that may be needed for a mortgage file. They should
                download their certificate (self-verifying PDF) <strong>before</strong> you delete — the
                record here is destroyed, but their exported PDF is not:
              </p>
              <p className="text-xs text-warning-800 font-medium mb-2">{attestedNames.join(', ')}</p>
              <label className="flex items-start gap-2 text-xs text-warning-800">
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
                <span>I understand these members should export their records first, and I want to proceed.</span>
              </label>
            </div>
          )}

          {/* Type-to-confirm */}
          <div>
            {/* FRANNIE DRAFT */}
            <label className="block text-xs font-medium text-neutral-600 mb-1">
              Type the group name <span className="font-semibold">{group.group_name}</span> to confirm
            </label>
            <input
              type="text"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={group.group_name}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-error-500 focus:border-transparent"
            />
          </div>

          {error && <p className="text-sm text-error-600">{error}</p>}
        </div>

        <div className="flex space-x-3 px-6 py-4 border-t border-neutral-200 flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!canDelete}
            className="flex-1 px-4 py-2 bg-error-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-error-700 disabled:opacity-50"
          >
            {working ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteGroupModal;
